import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  AutoAwesome as ChatIcon,
  Send as SendIcon,
  DeleteOutline as ClearIcon,
  Mic as MicIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "../../addressbook/panels/AddressbookPanelShell";
import { useWebLLM } from "../../../contexts/WebLLMContext";
import useSpeechToText from "../../../components/common/useSpeechToText";
import { apiGetComments, apiCreateComment } from "../../../graphQL/comment";
import { apiGetJournals, apiCreateJournal, apiUpdateJournal, apiCancelJournalReminder } from "../../../graphQL/journal";
import { apiUpdatePropertyAsset } from "../../../graphQL/properties/collections/propertyAssets";
import { apiGetContactById } from "../../../graphQL/addressbook/collections/contacts";

const REF_COLLECTION = "propertyAssets";

/* ── Context assembly ── */

function formatAddress(address) {
  if (!address) return "";
  const street = [address.streetNo, address.streetName].filter(Boolean).join(" ") || address.street || "";
  const parts = [street, address.unit && `Unit ${address.unit}`, address.city, address.state, address.zip].filter(Boolean);
  return parts.join(", ");
}

function extractPropertyContext(property) {
  if (!property) return "PROPERTY DETAILS:\nNo property is currently selected.";
  const identity = property.identity || {};
  const address = identity.address || {};
  const details = property.details || {};

  const lines = ["PROPERTY DETAILS:"];
  if (property.name) lines.push(`Name: ${property.name}`);
  if (identity.apn) lines.push(`APN: ${identity.apn}`);
  const addr = formatAddress(address);
  if (addr) lines.push(`Address: ${addr}`);
  if (property.status) lines.push(`Status: ${property.status}`);
  if (property.category) lines.push(`Category: ${property.category}`);

  // Pull commonly useful detail fields without making assumptions about exact shape.
  const detailKeys = ["propertyType", "useType", "subType", "yearBuilt", "lotSize", "buildingSize", "units", "bedrooms", "bathrooms", "price", "askingPrice", "rent", "zoning", "description", "notes"];
  detailKeys.forEach((k) => {
    const v = details?.[k];
    if (v !== undefined && v !== null && v !== "") lines.push(`${k}: ${v}`);
  });

  const geo = property.geolocation || {};
  if (geo.latitude != null && geo.longitude != null) {
    lines.push(`Geolocation: ${geo.latitude}, ${geo.longitude}`);
  }
  const publicUrls = Array.isArray(details?.publicUrls) ? details.publicUrls : [];
  if (publicUrls.length) lines.push(`Public files: ${publicUrls.length} (brochures/docs)`);

  const owner = details?.ownerRecord;
  if (owner && typeof owner === "object") {
    const ownerName = [owner.firstName, owner.lastName].filter(Boolean).join(" ") || owner.name || owner.company || "";
    const ownerAddr = owner.mailingAddress || owner.address || "";
    const ownerLine = [ownerName, ownerAddr].filter(Boolean).join(" — ");
    if (ownerLine) lines.push(`Owner of record: ${ownerLine}`);
  }

  const ingestedApns = Array.isArray(property.ingestedApns) ? property.ingestedApns : [];
  if (ingestedApns.length) {
    const apnSummary = ingestedApns.map((a) => `${a.value || a.apn || ""}${a.source ? ` (${a.source})` : ""}`).filter(Boolean).join("; ");
    if (apnSummary) lines.push(`Ingested APN candidates: ${apnSummary}`);
  }

  return lines.join("\n");
}

function formatLinkedContacts(property, linkedContacts) {
  const links = Array.isArray(property?.contactList) ? property.contactList : [];
  if (!links.length) return "";
  const lines = ["LINKED CONTACTS:"];
  const byId = new Map((linkedContacts || []).map((c) => [c?.id, c]));
  links.forEach((link) => {
    const c = byId.get(link?.contactId);
    if (!c) {
      lines.push(`- (unresolved contactId=${link?.contactId})${link?.role ? ` role=${link.role}` : ""}`);
      return;
    }
    const person = c.identity?.person || {};
    const company = c.identity?.company || {};
    const name = [person.firstName, person.lastName].filter(Boolean).join(" ") || company.name || "(unnamed)";
    const email = c.reach?.emails?.[0]?.value || "";
    const phone = c.reach?.phones?.[0]?.value || "";
    const parts = [name];
    if (link?.role) parts.push(`role=${link.role}`);
    if (company.name && name !== company.name) parts.push(`@${company.name}`);
    if (phone) parts.push(phone);
    if (email) parts.push(email);
    lines.push(`- ${parts.join(" — ")}`);
  });
  return lines.join("\n");
}

function safeJson(value) {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "{}";
  }
}

function extractJournalTextNotes(journal) {
  const jData = journal?.data ? (typeof journal.data === "string" ? JSON.parse(journal.data) : journal.data) : {};
  const notes = Array.isArray(jData.notes || jData.data?.notes) ? (jData.notes || jData.data?.notes) : [];
  return notes.filter((n) => {
    if (!n || typeof n !== "object") return false;
    if (!n.content || typeof n.content !== "string") return false;
    if (!n.type) return true;
    return n.type === "text" || n.type === "speech";
  });
}

function formatJournalNotes(notes) {
  if (!notes.length) return "";
  const lines = ["JOURNAL NOTES:"];
  notes.forEach((n, i) => {
    const date = n.createdAt ? new Date(n.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
    const prefix = date ? `[${date}]` : `[${i + 1}]`;
    lines.push(`${prefix} ${n.content || ""}`);
  });
  return lines.join("\n");
}

function formatComments(comments) {
  if (!comments.length) return "";
  const lines = ["COMMENTS:"];
  comments.forEach((c) => {
    const date = c.createdAt ? new Date(c.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
    const text = c.data?.text || "";
    lines.push(`[${date}] ${text}`);
  });
  return lines.join("\n");
}

function formatReminders(reminders) {
  if (!reminders.length) return "";
  const lines = ["REMINDERS:"];
  reminders.forEach((r) => {
    const data = typeof r.data === "string" ? JSON.parse(r.data) : (r.data || {});
    const action = data.action || "";
    const details = data.text || data.details || "";
    const state = r.state || "";
    const due = r.dueDate ? new Date(r.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
    lines.push(`[${due}] ${action}${details ? ": " + details : ""} (${state})`);
  });
  return lines.join("\n");
}

const TOOL_DEFINITIONS = `
TOOLS:
You have access to tools to take actions on this property. When the user asks you to create, update, or add something, respond with a tool call using this exact format on its own line:

[TOOL_CALL: tool_name({"param": "value"})]

Available tools:

1. create_reminder({"action": "review|inspect|tour|follow_up|schedule_showing", "amount": number, "unit": "minute|hour|day|week|month|year", "text": "optional details", "time": "HH:MM (optional, 24h format)"})
   - Creates a reminder for this property. If the user specifies a time, include the "time" field (e.g. "14:30" in 24h). If no time is specified, omit it (defaults to 8:00 AM for day or longer units). Use amount=0 when the user wants a reminder TODAY at a specific time.
   - CRITICAL: Only include "text" if the user explicitly provides details. NEVER invent or make up text.
   - Example: [TOOL_CALL: create_reminder({"action": "inspect", "amount": 3, "unit": "day"})]
   - Example with details: [TOOL_CALL: create_reminder({"action": "follow_up", "amount": 1, "unit": "week", "text": "Confirm offer terms"})]

2. add_comment({"text": "the comment text"})
   - Adds a comment to this property.
   - Example: [TOOL_CALL: add_comment({"text": "Toured the site, roof needs work"})]

3. update_property({"field": "field_path", "value": "new value"})
   - Updates a field on this property. Valid fields: name, status, category, identity.apn, identity.address.streetNo, identity.address.streetName, identity.address.unit, identity.address.city, identity.address.state, identity.address.zip, details.notes, details.description, details.propertyType, details.useType, details.yearBuilt, details.lotSize, details.buildingSize, details.units, details.price, details.askingPrice, details.rent, details.zoning
   - Example: [TOOL_CALL: update_property({"field": "status", "value": "Under Contract"})]

4. update_reminder({"dueDate": "ISO date string", "action": "...", "details": "reminder details text", "state": "done"})
   - Updates the currently selected reminder. Only available when a reminder is selected. Only include the fields you are actually changing.
   - CRITICAL: Do NOT include "dueDate" unless the user explicitly asks to change the date/time.
   - "state" can ONLY be set to "done" when the user EXPLICITLY says "mark as done", "mark it done", "complete it", or "cancel the reminder".
   - For dueDate, use LOCAL time not UTC (e.g. "2026-04-23T17:00:00", no Z suffix).

IMPORTANT: Always include a brief text response before or after the tool call explaining what you did. Only use one tool call per response. If the user's request is ambiguous, ask for clarification instead of guessing.

CRITICAL RULES — DO NOT VIOLATE:
- ONLY output English text. NEVER output Chinese, Japanese, or any non-English characters.
- Tool calls must be EXACTLY the format shown: [TOOL_CALL: name({"key": "value"})] — nothing else inside the brackets.
- Do NOT add extra text, translations, timestamps, or annotations inside the [TOOL_CALL: ...] brackets.
- Do NOT hallucinate, invent, or fabricate any data. Only use information from the context provided above.
- If you are unsure about a value, ASK the user instead of guessing.
`;

function formatSelectedReminder(reminder) {
  if (!reminder) return "";
  const data = typeof reminder.data === "string" ? JSON.parse(reminder.data) : (reminder.data || {});
  const lines = ["SELECTED REMINDER (you can update this with update_reminder):"];
  lines.push(`ID: ${reminder.id}`);
  lines.push(`Action: ${data.action || "unknown"}`);
  lines.push(`Due: ${reminder.dueDate || "not set"}`);
  lines.push(`State: ${reminder.state || "created"}`);
  lines.push(`Details: ${data.text || "(none)"}`);
  return lines.join("\n");
}

function buildSystemPrompt(propertyCtx, journalCtx, commentsCtx, remindersCtx, selectedReminderCtx, linkedContactsCtx) {
  const sections = [
    "You are a helpful assistant that answers questions about a specific commercial real estate property based on the context provided below.",
    "You can also take actions like creating reminders, adding comments, updating property fields, and updating the selected reminder.",
    "STRICT RULE: Only use information from the context provided. NEVER make up, guess, or infer data that is not explicitly present in the context. If a field is empty, missing, or not mentioned in the context, say you don't have that information. Do not fabricate addresses, prices, dates, or any other details.",
    "Be concise and direct.",
    "",
    TOOL_DEFINITIONS,
    "",
    propertyCtx,
  ];
  if (linkedContactsCtx) sections.push("", linkedContactsCtx);
  if (journalCtx) sections.push("", journalCtx);
  if (commentsCtx) sections.push("", commentsCtx);
  if (remindersCtx) sections.push("", remindersCtx);
  if (selectedReminderCtx) sections.push("", selectedReminderCtx);
  return sections.join("\n");
}

function buildPropertyContext(property) {
  return [
    extractPropertyContext(property),
    "",
    "FULL PROPERTY JSON (authoritative):",
    safeJson(property),
  ].join("\n");
}

/* ── Tool call parser & executor ── */

const TOOL_CALL_REGEX = /\[TOOL_CALL:\s*(\w+)\((\{[\s\S]*?\})\)[^\]]*\]/;

function parseToolCall(text) {
  const match = text.match(TOOL_CALL_REGEX);
  if (!match) return null;
  try {
    const name = match[1];
    const args = JSON.parse(match[2]);
    return { name, args, raw: match[0] };
  } catch {
    return null;
  }
}

function stripToolCall(text) {
  return text.replace(TOOL_CALL_REGEX, "").trim();
}

function setTimeTo8am(date) {
  const d = new Date(date);
  d.setHours(8, 0, 0, 0);
  return d;
}

function addDuration(baseDate, amount, unit) {
  const d = new Date(baseDate);
  const n = Number(amount);
  if (!n || n <= 0) return d;
  if (unit === "minute") { d.setMinutes(d.getMinutes() + n); return d; }
  if (unit === "hour") { d.setHours(d.getHours() + n); return d; }
  if (unit === "day") { d.setDate(d.getDate() + n); return d; }
  if (unit === "week") { d.setDate(d.getDate() + n * 7); return d; }
  if (unit === "month") { d.setMonth(d.getMonth() + n); return d; }
  if (unit === "year") { d.setFullYear(d.getFullYear() + n); return d; }
  return d;
}

const PROPERTY_UPDATE_FIELDS = new Set([
  "name", "status", "category",
  "identity.apn", "identity.address.streetNo", "identity.address.streetName",
  "identity.address.unit", "identity.address.city", "identity.address.state", "identity.address.zip",
  "details.notes", "details.description", "details.propertyType", "details.useType",
  "details.yearBuilt", "details.lotSize", "details.buildingSize", "details.units",
  "details.price", "details.askingPrice", "details.rent", "details.zoning",
]);

function deepSet(obj, path, value) {
  const clone = JSON.parse(JSON.stringify(obj || {}));
  const keys = path.split(".");
  let cursor = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    if (cursor[keys[i]] == null || typeof cursor[keys[i]] !== "object") cursor[keys[i]] = {};
    cursor = cursor[keys[i]];
  }
  cursor[keys[keys.length - 1]] = value;
  return clone;
}

async function executeToolCall(tool, { property, authorId, selectedReminder, activeDomain }) {
  const { name, args } = tool;
  const propertyId = property?.id;
  const domainArg = activeDomain ? { domain: activeDomain } : {};
  const propertyLabel = (() => {
    const a = property?.identity?.address || {};
    return [a.streetNo, a.streetName].filter(Boolean).join(" ") || a.street || property?.name || property?.identity?.apn || "";
  })();

  if (name === "create_reminder") {
    if (!propertyId) return "Error: no property is selected. Please select a property first.";
    const unit = args.unit || "day";
    const isShortDuration = unit === "minute" || unit === "hour";
    const now = new Date();
    let dueDate;
    if (args.time) {
      const [h, m] = args.time.split(":").map(Number);
      const todayAtTime = new Date(now);
      todayAtTime.setHours(h || 0, m || 0, 0, 0);
      if (!args.amount || args.amount === 0) {
        if (todayAtTime > now) dueDate = todayAtTime;
        else { todayAtTime.setDate(todayAtTime.getDate() + 1); dueDate = todayAtTime; }
      } else {
        const rawDue = addDuration(now, args.amount, unit);
        dueDate = new Date(rawDue);
        dueDate.setHours(h || 0, m || 0, 0, 0);
      }
    } else if (isShortDuration) {
      dueDate = addDuration(now, args.amount || 1, unit);
    } else {
      dueDate = setTimeTo8am(addDuration(now, args.amount || 1, unit));
    }
    await apiCreateJournal({
      authorId,
      ref: { id: propertyId, collection: REF_COLLECTION },
      type: "reminder",
      dueDate: dueDate.toISOString(),
      ...domainArg,
      data: {
        amount: args.amount || 1,
        unit: args.unit || "day",
        days: args.amount || 1,
        action: args.action || "follow_up",
        text: args.text || "",
        propertyId,
        propertyLabel,
      },
    });
    return `Reminder created: ${args.action} in ${args.amount} ${args.unit}(s)`;
  }

  if (name === "add_comment") {
    if (!args.text) return "Error: comment text is required";
    await apiCreateComment({
      authorId,
      data: { text: args.text },
      ref: { collection: REF_COLLECTION, id: propertyId },
    });
    return `Comment added: "${args.text}"`;
  }

  if (name === "update_property") {
    if (!args.field || args.value === undefined) return "Error: field and value are required";
    if (!PROPERTY_UPDATE_FIELDS.has(args.field)) return `Error: invalid field "${args.field}"`;
    const clone = deepSet(property, args.field, args.value);
    await apiUpdatePropertyAsset({
      id: propertyId,
      name: clone.name,
      status: clone.status,
      category: clone.category,
      identity: clone.identity,
      details: clone.details,
    });
    return `Updated ${args.field} to "${args.value}"`;
  }

  if (name === "update_reminder") {
    if (!selectedReminder) return "Error: no reminder is selected. Please select a reminder first.";
    if (args.state === "done") {
      await apiCancelJournalReminder(selectedReminder.id);
      const existData = typeof selectedReminder.data === "string" ? JSON.parse(selectedReminder.data) : (selectedReminder.data || {});
      const doneData = { ...existData, status: "done" };
      const persisted = await apiUpdateJournal({ id: selectedReminder.id, data: doneData });
      const updatedReminder = persisted?.journal || { ...selectedReminder, data: doneData };
      return { message: "Reminder marked as done and agenda job cancelled.", updatedReminder };
    }
    const detailsValue = args.details !== undefined ? args.details : args.text;
    const updateInput = { id: selectedReminder.id };
    if (args.dueDate) {
      const raw = args.dueDate;
      const hasTimezone = /Z|[+-]\d{2}:\d{2}$/.test(raw);
      updateInput.dueDate = hasTimezone ? raw : new Date(raw).toISOString();
    }
    const existingData = typeof selectedReminder.data === "string"
      ? JSON.parse(selectedReminder.data) : (selectedReminder.data || {});
    const newData = { ...existingData };
    if (args.action) newData.action = args.action;
    if (detailsValue !== undefined) newData.text = detailsValue;
    updateInput.data = newData;
    const result = await apiUpdateJournal(updateInput);
    const parts = [];
    if (args.dueDate) parts.push(`due date to ${new Date(updateInput.dueDate).toLocaleString()}`);
    if (args.action) parts.push(`action to ${args.action}`);
    if (detailsValue !== undefined) parts.push(`details to "${detailsValue}"`);
    const updatedReminder = result?.journal || { ...selectedReminder, ...updateInput };
    return { message: `Reminder updated: ${parts.join(", ")}`, updatedReminder };
  }

  return `Unknown tool: ${name}`;
}

/* ── Message bubble ── */

const MessageBubble = ({ role, content, streaming }) => (
  <Box sx={{
    display: "flex",
    justifyContent: role === "user" ? "flex-end" : "flex-start",
    mb: 0.75,
  }}>
    <Box sx={{
      maxWidth: "85%",
      px: 1.5,
      py: 1,
      borderRadius: role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
      backgroundColor: role === "user" ? "#3B82F6" : "#F1F5F9",
      color: role === "user" ? "#fff" : "#1E293B",
    }}>
      <Typography sx={{ fontSize: 13, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
        {content}
        {streaming && <Box component="span" sx={{ animation: "blink 1s steps(2) infinite", "@keyframes blink": { "50%": { opacity: 0 } } }}> |</Box>}
      </Typography>
    </Box>
  </Box>
);

/* ── Main panel ── */

const PropertyChatPanel = ({ property, authorId, onClose, onActionComplete, selectedReminder }) => {
  const { engine, ready: llmReady, loading: llmLoading, downloading, progress, error: llmError, init: initLLM, resetChat } = useWebLLM();

  useEffect(() => { initLLM(); }, [initLLM]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const appendDictation = useCallback((text) => {
    setInput((prev) => (prev ? `${prev} ${text}` : text));
  }, []);
  const { supported: sttSupported, isListening, interimTranscript, toggle: toggleDictation, stop: stopDictation } =
    useSpeechToText({ onFinalText: appendDictation });
  const [generating, setGenerating] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [basePrompt, setBasePrompt] = useState("");
  const [tokenUsage, setTokenUsage] = useState(null);
  const listRef = useRef(null);
  const abortRef = useRef(false);

  const propertyId = property?.id;

  // Active domain for silent journal isolation (never rendered).
  const reduxDomain = useSelector((state) => state.auth.user?.domain || "");
  const activeDomain = reduxDomain && reduxDomain !== "All" ? reduxDomain : "";

  const systemPrompt = selectedReminder
    ? basePrompt + "\n\n" + formatSelectedReminder(selectedReminder)
    : basePrompt;

  useEffect(() => {
    resetChat();
    setMessages([]);
  }, [propertyId, resetChat]);

  useEffect(() => {
    if (!propertyId) {
      setBasePrompt(buildSystemPrompt("No property is currently selected. You can still answer general questions.", "", "", ""));
      return;
    }
    let cancelled = false;
    setContextLoading(true);
    (async () => {
      try {
        const fetches = [
          apiGetComments({
            refCollection: REF_COLLECTION,
            refId: propertyId,
            ordering: { field: "createdAt", order: "Asc" },
            pagination: { page: 1, limit: 500 },
          }),
        ];
        if (authorId) {
          fetches.push(
            apiGetJournals({ authorId, refId: propertyId, refCollection: REF_COLLECTION, type: "journal", ...(activeDomain ? { domain: activeDomain } : {}) }),
            apiGetJournals({ authorId, refId: propertyId, refCollection: REF_COLLECTION, type: "reminder", ...(activeDomain ? { domain: activeDomain } : {}) }),
          );
        }

        const contactIds = (Array.isArray(property?.contactList) ? property.contactList : [])
          .map((e) => e?.contactId)
          .filter(Boolean);
        const linkedContactsPromise = contactIds.length
          ? Promise.all(contactIds.map((id) => apiGetContactById({ id, authorId: authorId || null }).catch(() => null)))
          : Promise.resolve([]);

        const [results, linkedContacts] = await Promise.all([Promise.all(fetches), linkedContactsPromise]);
        if (cancelled) return;

        const commentsRes = results[0];
        const journalRes = authorId ? results[1] : null;
        const reminderRes = authorId ? results[2] : null;

        const propertyCtx = buildPropertyContext(property);
        const j = journalRes?.journals?.[0];
        const textNotes = extractJournalTextNotes(j);
        const reminders = reminderRes?.journals || [];
        const comments = commentsRes?.yrhComments || [];

        const prompt = buildSystemPrompt(
          propertyCtx,
          formatJournalNotes(textNotes),
          formatComments(comments),
          formatReminders(reminders),
          "",
          formatLinkedContacts(property, linkedContacts.filter(Boolean)),
        );
        setBasePrompt(prompt);
      } catch (err) {
        console.error("Failed to load chat context:", err);
        setBasePrompt(buildSystemPrompt(buildPropertyContext(property), "", "", ""));
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [propertyId, authorId, property, activeDomain]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const handleSend = useCallback(async () => {
    stopDictation();
    const text = input.trim();
    if (!text || !engine || !systemPrompt || generating) return;
    const userMsg = { role: "user", content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput("");
    setGenerating(true);
    abortRef.current = false;

    const llmMessages = [
      { role: "system", content: systemPrompt },
      ...updatedMessages.map((m) => ({ role: m.role, content: m.content })),
    ];

    try {
      const chunks = await engine.chat.completions.create({
        messages: llmMessages,
        temperature: 0.3,
        top_p: 0.85,
        max_tokens: 500,
        stream: true,
        stream_options: { include_usage: true },
      });

      let accumulated = "";
      let lastUsage = null;
      setMessages((prev) => [...prev, { role: "assistant", content: "", streaming: true }]);

      for await (const chunk of chunks) {
        if (abortRef.current) break;
        const delta = chunk.choices?.[0]?.delta?.content || "";
        if (delta) {
          accumulated += delta;
          const current = accumulated;
          setMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = { role: "assistant", content: current, streaming: true };
            return copy;
          });
        }
        if (chunk.usage) lastUsage = chunk.usage;
      }
      if (lastUsage) setTokenUsage(lastUsage);

      const final = accumulated;
      const toolCall = parseToolCall(final);
      if (toolCall && authorId) {
        const displayText = stripToolCall(final).trim();
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = {
            role: "assistant",
            content: displayText || "Processing...",
            streaming: true,
          };
          return copy;
        });
        try {
          const result = await executeToolCall(toolCall, { property, authorId, selectedReminder, activeDomain });
          const resultMsg = typeof result === "string" ? result : result.message;
          const confirmMsg = `✓ ${resultMsg}`;
          setMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = { role: "assistant", content: displayText || confirmMsg };
            if (displayText) copy.push({ role: "assistant", content: confirmMsg });
            return copy;
          });
          const updatedReminder = typeof result === "object" ? result.updatedReminder : null;
          if (onActionComplete) onActionComplete(toolCall.name, updatedReminder);
        } catch (toolErr) {
          console.error("Tool execution failed:", toolErr);
          setMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = { role: "assistant", content: displayText || "I tried to perform the action but it failed." };
            copy.push({ role: "assistant", content: `✗ Action failed: ${toolErr.message || "Unknown error"}` });
            return copy;
          });
        }
      } else {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: final };
          return copy;
        });
      }
    } catch (err) {
      console.error("Chat generation failed:", err);
      setMessages((prev) => [...prev.filter((m) => !m.streaming), { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setGenerating(false);
    }
  }, [input, engine, systemPrompt, messages, generating, property, authorId, selectedReminder, onActionComplete, stopDictation, activeDomain]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    abortRef.current = true;
    setMessages([]);
    setGenerating(false);
    resetChat();
    setTokenUsage(null);
  };

  const propertyLabel = (() => {
    const a = property?.identity?.address || {};
    return [a.streetNo, a.streetName].filter(Boolean).join(" ") || a.street || property?.name || property?.identity?.apn || "";
  })();

  const reminderLabel = (() => {
    if (!selectedReminder) return "";
    const data = typeof selectedReminder.data === "string"
      ? JSON.parse(selectedReminder.data) : (selectedReminder.data || {});
    const action = data.action || "reminder";
    const due = selectedReminder.dueDate
      ? new Date(selectedReminder.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
      : "";
    return `${action}${due ? " — " + due : ""}`;
  })();

  const canSend = input.trim() && llmReady && systemPrompt && !generating;

  const llmStatus = (() => {
    if (llmError) return "AI model failed to load. Check browser console.";
    if (downloading) return `Downloading AI model... ${Math.round(progress * 100)}%`;
    if (llmLoading) return "Loading AI model...";
    if (!llmReady) return "Waiting for AI model...";
    return null;
  })();

  return (
    <AddressbookPanelShell
      onClose={onClose}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 0.5 }}>
          <ChatIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>Chat</Typography>
          {!llmReady && !llmError && (
            <CircularProgress size={10} sx={{ ml: 0.5, color: "#7c3aed" }} />
          )}
        </Box>
      }
      headerActions={
        messages.length > 0 ? (
          <IconButton size="small" onClick={handleClear} title="Clear chat" sx={{ color: "#9CA3AF", "&:hover": { color: "#6B7280" } }}>
            <ClearIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null
      }
      footerLeft={
        (propertyLabel || reminderLabel) ? (
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            {[propertyLabel, reminderLabel].filter(Boolean).join(" · ")}
          </Typography>
        ) : null
      }
      footerRight={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {tokenUsage && (
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: tokenUsage.total_tokens > 24000 ? "#EF4444" : tokenUsage.total_tokens > 16000 ? "#F59E0B" : "#9CA3AF" }}>
              {tokenUsage.total_tokens.toLocaleString()} / 32K
            </Typography>
          )}
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            {messages.length} entr{messages.length === 1 ? "y" : "ies"}
          </Typography>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <Box ref={listRef} sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
          {contextLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", py: 3 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Loading context...</Typography>
            </Box>
          )}
          {!contextLoading && messages.length === 0 && (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <ChatIcon sx={{ fontSize: 32, color: "#E2E8F0", mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.5 }}>
                {propertyLabel && selectedReminder
                  ? `Ask anything about ${propertyLabel} or the selected reminder`
                  : propertyLabel
                    ? `Ask anything about ${propertyLabel}`
                    : selectedReminder
                      ? "Ask anything about the selected reminder"
                      : "Ask me anything"}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#94A3B8", lineHeight: 1.5 }}>
                {propertyLabel
                  ? "Its details, journal notes, comments, and reminders are all included as context."
                  : "Select a property to get started."}
              </Typography>
            </Box>
          )}
          {messages.map((msg, i) => (
            <MessageBubble key={i} role={msg.role} content={msg.content} streaming={msg.streaming} />
          ))}
        </Box>

        <Box sx={{ p: 1, borderTop: "1px solid", borderColor: "divider", backgroundColor: "#FAFBFC", flexShrink: 0 }}>
          {llmStatus && (
            <Typography sx={{ fontSize: 11, color: llmError ? "#DC2626" : "#9CA3AF", mb: 0.5, textAlign: "center" }}>
              {llmStatus}
            </Typography>
          )}
          {isListening && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, px: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#3F51B5", animation: "cs-pulse 1s ease-in-out infinite", "@keyframes cs-pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } } }} />
              <Typography sx={{ fontSize: 11, color: "#3F51B5" }}>
                Listening{interimTranscript ? `: ${interimTranscript}` : "..."}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-end" }}>
            <Tooltip title={sttSupported ? (isListening ? "Stop dictation" : "Dictate with your voice") : "Voice input not supported in this browser"}>
              <span>
                <IconButton
                  size="small"
                  onClick={toggleDictation}
                  disabled={!sttSupported || !llmReady || contextLoading}
                  aria-label={isListening ? "Stop dictation" : "Start voice dictation"}
                  sx={{
                    color: isListening ? "#3F51B5" : "#536DFE",
                    "&:hover": { color: "#3949AB", backgroundColor: "#E8EAF6" },
                    p: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {isListening ? <StopIcon sx={{ fontSize: 18 }} /> : <MicIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder={llmReady ? (propertyLabel ? `Ask about ${propertyLabel}${selectedReminder ? " or reminder" : ""}...` : "Ask me anything...") : "Waiting for AI model..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!llmReady || contextLoading}
              size="small"
              sx={{ "& .MuiInputBase-root": { fontSize: 13, backgroundColor: "#fff" } }}
            />
            <IconButton
              size="small"
              onClick={handleSend}
              disabled={!canSend}
              sx={{
                color: canSend ? "#7c3aed" : "#D1D5DB",
                "&:hover": { color: "#6D28D9" },
                p: 0.5,
                flexShrink: 0,
              }}
            >
              {generating ? <CircularProgress size={18} sx={{ color: "#7c3aed" }} /> : <SendIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </AddressbookPanelShell>
  );
};

export default PropertyChatPanel;
